/**
 * Shared phone-number formatting helpers used by /login and the
 * QuickAuthModal register/signin flow.
 *
 * • COUNTRY_CODES — options in the country picker (ordered by user base)
 * • NATIONAL_FORMATS — per-dial-code display template + max digits + hint
 * • formatNational(digits, cc) — turns raw digits into a friendly display
 *   string like "(555) 123-4567" for +1 or "98765 43210" for +91.
 * • sanitizeToE164(countryCode, nationalDigits) — final E.164 string
 *   Firebase's signInWithPhoneNumber expects. Strips all formatting.
 */

export const COUNTRY_CODES = [
  { code: "+1",   flag: "🇺🇸", label: "USA" },
  { code: "+91",  flag: "🇮🇳", label: "India" },
  { code: "+44",  flag: "🇬🇧", label: "UK" },
  { code: "+1",   flag: "🇨🇦", label: "Canada", key: "ca" },
  { code: "+61",  flag: "🇦🇺", label: "Australia" },
  { code: "+971", flag: "🇦🇪", label: "UAE" },
  { code: "+65",  flag: "🇸🇬", label: "Singapore" },
];

export const NATIONAL_FORMATS = {
  "+1":   { template: "(XXX) XXX-XXXX", maxLen: 10, hint: "(555) 123-4567" },
  "+91":  { template: "XXXXX XXXXX",    maxLen: 10, hint: "98765 43210" },
  "+44":  { template: "XXXX XXX XXX",   maxLen: 10, hint: "7700 900 123" },
  "+61":  { template: "XXX XXX XXX",    maxLen: 9,  hint: "412 345 678" },
  "+971": { template: "XX XXX XXXX",    maxLen: 9,  hint: "50 123 4567" },
  "+65":  { template: "XXXX XXXX",      maxLen: 8,  hint: "9123 4567" },
};

export function formatNational(digits, cc) {
  const d = (digits || "").replace(/\D/g, "");
  if (!d) return "";
  const fmt = NATIONAL_FORMATS[cc];
  if (!fmt) {
    // Unknown country — group digits by 4 for readability.
    return d.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  }
  const clip = d.slice(0, fmt.maxLen);
  // Country-specific piecewise formatting — much easier to reason about
  // than a general template state-machine and always produces expected
  // intermediate strings ("(555" → "(555) 12" → "(555) 123-4567").
  if (cc === "+1") {
    if (clip.length <= 3) return `(${clip}`;
    if (clip.length <= 6) return `(${clip.slice(0, 3)}) ${clip.slice(3)}`;
    return `(${clip.slice(0, 3)}) ${clip.slice(3, 6)}-${clip.slice(6)}`;
  }
  if (cc === "+91") {
    if (clip.length <= 5) return clip;
    return `${clip.slice(0, 5)} ${clip.slice(5)}`;
  }
  if (cc === "+44") {
    if (clip.length <= 4) return clip;
    if (clip.length <= 7) return `${clip.slice(0, 4)} ${clip.slice(4)}`;
    return `${clip.slice(0, 4)} ${clip.slice(4, 7)} ${clip.slice(7)}`;
  }
  if (cc === "+61") {
    if (clip.length <= 3) return clip;
    if (clip.length <= 6) return `${clip.slice(0, 3)} ${clip.slice(3)}`;
    return `${clip.slice(0, 3)} ${clip.slice(3, 6)} ${clip.slice(6)}`;
  }
  if (cc === "+971") {
    if (clip.length <= 2) return clip;
    if (clip.length <= 5) return `${clip.slice(0, 2)} ${clip.slice(2)}`;
    return `${clip.slice(0, 2)} ${clip.slice(2, 5)} ${clip.slice(5)}`;
  }
  if (cc === "+65") {
    if (clip.length <= 4) return clip;
    return `${clip.slice(0, 4)} ${clip.slice(4)}`;
  }
  // Fallback: group by 4
  return clip.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

export function sanitizeToE164(countryCode, nationalDigits) {
  const cc = (countryCode || "").replace(/[^\d+]/g, "");
  const withPlus = cc.startsWith("+") ? cc : (cc ? "+" + cc : "");
  const digits = (nationalDigits || "").replace(/\D/g, "");
  return withPlus + digits;
}
